import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ContactOfficeDetails() {
  const details = [
    { icon: 'ri-map-pin-2-line', label: 'Office Address', lines: ['Limassol, Cyprus'], link: 'https://maps.google.com/?q=Limassol,+Cyprus', linkLabel: 'Get Directions' },
    { icon: 'ri-phone-line', label: 'Phone', lines: ['+357 95981879'], link: 'tel:+35795981879', linkLabel: 'Call Us' },
    { icon: 'ri-whatsapp-line', label: 'WhatsApp', lines: ['+357 95981879'], link: 'https://wa.me/35795981879', linkLabel: 'Message Us' },
    { icon: 'ri-time-line', label: 'Business Hours', lines: ['Mon – Fri: 9:00 AM – 6:00 PM', 'Sat – Sun: Closed'], link: null, linkLabel: null },
  ];

  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation();
  const { ref: socialRef, isVisible: socialVisible } = useScrollAnimation();
  const [visibleCards, setVisibleCards] = useState<boolean[]>(Array(details.length).fill(false));

  useEffect(() => {
    if (!gridVisible) return;
    details.forEach((_, i) => {
      setTimeout(() => {
        setVisibleCards((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, 100 * i);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridVisible]);

  return (
    <div className="space-y-5">
      <div
        ref={headRef as React.RefObject<HTMLDivElement>}
        className={`transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        <h2 className="text-2xl font-semibold text-slate-900">Office Details</h2>
        <p className="text-slate-600 text-sm leading-relaxed mt-1">
          Based in the heart of Limassol, Cyprus — one of the Mediterranean's leading financial and business hubs.
        </p>
      </div>

      <div
        ref={gridRef as React.RefObject<HTMLDivElement>}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        {details.map((item, i) => (
          <div
            key={item.label}
            className={`bg-white rounded-xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all ${
              visibleCards[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDuration: '550ms', transitionProperty: 'opacity, transform, box-shadow' }}
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-50 shrink-0">
                <i className={`${item.icon} text-amber-500 text-xl`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">{item.label}</p>
                {item.lines.map((line) => (
                  <p key={line} className="text-slate-800 font-medium text-sm leading-snug">{line}</p>
                ))}
                {item.link && (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-amber-500 text-xs font-semibold hover:text-amber-600 transition-colors cursor-pointer"
                  >
                    {item.linkLabel}
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Social Links */}
      <div
        ref={socialRef as React.RefObject<HTMLDivElement>}
        className={`pt-2 transition-all duration-700 ${socialVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Follow Us</p>
        <div className="flex gap-3">
          {[
            { href: 'https://www.facebook.com/profile.php?id=61563327389236', icon: 'ri-facebook-fill', label: 'Facebook' },
            { href: 'https://www.linkedin.com/company/cjsolutionsltd/', icon: 'ri-linkedin-fill', label: 'LinkedIn' },
            { href: 'https://x.com/CJSolutionsltd', icon: 'ri-twitter-x-fill', label: 'Twitter / X' },
          ].map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-amber-50 hover:border-amber-300 transition-colors cursor-pointer"
              aria-label={s.label}
            >
              <i className={`${s.icon} text-slate-700 text-lg`}></i>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
